import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';

import { useOutsideClick } from '@/hooks/ui/useOutsideClick';
import { useDebounced } from '@/hooks/ui/useDebounced';
import { fetchTasks } from '@/entities/task/api/tasks';
import { getTaskDisplayName } from '@/utils/displayUtils';
import { FloatingPanel } from '@/shared/ui/FloatingPanel';
import type { TaskOptionItem } from '../types';
import { onAltArrowDownOpen } from '../utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ANY = any;

interface DTaskLinkDropdownProps {
  disabled?: boolean;
  tasks: TaskOptionItem[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  placeholder?: string;
  /** Which edge the panel is anchored to (so it stays inside the modal). */
  align?: 'left' | 'right';
  /** Задача, которую нельзя выбрать (текущая — при редактировании). */
  excludeId?: number | null;
  /** Вид триггера: белая плашка (по умолчанию) или подчёркнутый текст макета. */
  variant?: 'pill' | 'proto';
}

export function DTaskLinkDropdown({
  disabled,
  tasks,
  selectedId,
  onSelect,
  placeholder = 'Выбор задачи',
  align = 'left',
  excludeId = null,
  variant = 'pill',
}: DTaskLinkDropdownProps) {
  const isProto = variant === 'proto';
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q.trim(), 400);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useOutsideClick({
    enabled: open,
    refs: [btnRef as ANY, panelRef as ANY],
    onOutside: () => setOpen(false),
  });

  const selectedTask = useMemo(() => {
    if (!selectedId) {
      return null;
    }
    return tasks.find((t) => t.id === selectedId) ?? null;
  }, [tasks, selectedId]);

  // Пустой запрос — показываем предзагруженный дефолтный список (как раньше).
  // Непустой запрос — ищем на бэке через /tasks/list?search=..., а не по
  // локально обрезанным 500 задачам: иначе задачи за пределами этой пачки
  // никогда не находились (баг, который чинит эта серверная выдача).
  const searchActive = open && debouncedQ.length > 0;

  const { data: searchData, isFetching: isSearching } = useQuery({
    queryKey: ['task-link-search', debouncedQ],
    queryFn: () =>
      fetchTasks({
        search: debouncedQ,
        serverPaginated: true,
        page: 1,
        perPage: 50,
      }),
    enabled: searchActive,
    staleTime: 30 * 1000,
  });

  const searched = useMemo<TaskOptionItem[]>(() => {
    const items = searchData?.items;
    if (!Array.isArray(items)) {
      return [];
    }
    return items
      .map((t) => {
        const id = Number(t.id ?? 0);
        if (!Number.isFinite(id) || id <= 0) {
          return null;
        }
        if (excludeId && id === excludeId) {
          return null;
        }
        const title = getTaskDisplayName(t).trim() || `Задача #${id}`;
        return { id, title } satisfies TaskOptionItem;
      })
      .filter(Boolean) as TaskOptionItem[];
  }, [searchData, excludeId]);

  const filtered = searchActive ? searched : tasks;
  const showLoading = searchActive && isSearching;

  useEffect(() => {
    if (!open) {
      return;
    }
    setQ('');
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  const label = selectedTask ? selectedTask.title : placeholder;

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onAltArrowDownOpen(open, () => setOpen(true))}
        className={clsx(
          isProto
            ? [
                'inline-flex max-w-[220px] items-center gap-[7px]',
                'border-b border-[rgba(59,77,127,0.3)] pb-[4px] pt-[3px]',
                'transition-colors hover:border-[#57B792]',
              ]
            : [
                'inline-flex h-[35px] items-center gap-2 rounded-[8.36px] bg-white px-[12px]',
                'shadow-[0_3.54px_12.88px_rgba(0,0,0,0.03)] border border-black/5',
              ],
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        aria-expanded={open}
        aria-haspopup="menu"
        title={label}
      >
        <span
          className={clsx(
            'min-w-0 truncate',
            isProto
              ? 'text-[12px] font-semibold text-[#141E31]'
              : 'max-w-[180px] text-[14px] tracking-[-0.03em] text-[#14222C] underline'
          )}
        >
          {label}
        </span>
        <ChevronDown
          className={clsx(
            'transition',
            isProto
              ? 'w-[12px] h-[12px] shrink-0 text-[rgba(20,30,49,0.45)]'
              : 'w-[14px] h-[14px] text-[#14222C]',
            open && 'rotate-180'
          )}
        />
      </button>

      {open ? (
        <FloatingPanel
          ref={panelRef}
          anchorRef={wrapRef}
          align={align === 'right' ? 'end' : 'start'}
          sideOffset={8}
          className="w-[380px] max-w-[calc(100vw-48px)] rounded-[12px] border border-black/10 bg-white p-2 shadow-[0_12px_30px_rgba(0,0,0,0.18)]"
          role="menu"
        >
          <div className="flex items-center gap-2 pb-2">
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Поиск по названию..."
              className="h-[32px] w-full rounded-[10px] border border-black/10 bg-[#F8F7FC] px-3 text-[13px] text-[#14222C] outline-none placeholder:text-black/40 focus:border-[#57B79255]"
            />
            {selectedId ? (
              <button
                type="button"
                className="h-[32px] shrink-0 rounded-[10px] border border-black/10 px-3 text-[12px] text-black/70 transition hover:bg-black/5"
                onClick={() => {
                  onSelect(null);
                  setOpen(false);
                }}
                title="Снять связь"
              >
                Снять
              </button>
            ) : null}
          </div>

          <div className="max-h-[300px] overflow-auto pr-1">
            {showLoading && filtered.length === 0 ? (
              <div className="flex items-center gap-2 px-2 py-3 text-[13px] text-black/50">
                <Loader2 className="h-[14px] w-[14px] animate-spin" />
                Ищу...
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-2 py-3 text-[13px] text-black/50">
                Ничего не найдено
              </div>
            ) : (
              <div className="space-y-1">
                {filtered.map((t) => {
                  const active = selectedId === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={clsx(
                        'w-full rounded-[10px] px-2 py-2 text-left transition',
                        active ? 'bg-[#57B7921A]' : 'hover:bg-black/5'
                      )}
                      onClick={() => {
                        onSelect(t.id);
                        setOpen(false);
                      }}
                      role="menuitem"
                      title={t.title}
                    >
                      <div className="truncate text-[13px] font-medium text-[#14222C]">
                        {t.title}
                      </div>
                      <div className="mt-0.5 text-[11px] text-black/45">
                        ID: {t.id}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </FloatingPanel>
      ) : null}
    </div>
  );
}
